import { useState } from 'react';
import BottomSheet from '../../../../components/ui/BottomSheet';
import Button from '../../../../components/ui/Button';

/**
 * Finish session bottom sheet.
 *
 * Default state: progress line + action rows (Export Tests / Session email)
 * + "Complete & Return to Dashboard" + "Cancel".
 *
 * Gate: tapping Complete when remaining > 0 reveals an inline warning block
 * ("28 not tested — finish anyway?") with Go back / Finish anyway.
 * At remaining = 0 the button completes immediately.
 *
 * Props:
 *   open            — controlled open state
 *   onClose         — close / Cancel handler
 *   totalAssets     — total asset count in this session
 *   remaining       — untested count
 *   isFinishing     — true while the finish API call is in flight
 *   onExportTests   — opens the Export Tests sheet
 *   onSessionEmail  — stub for session email (future)
 *   onComplete      — performs the actual finish + navigation
 */
export default function FinishSessionSheet({
  open,
  onClose,
  totalAssets = 0,
  remaining = 0,
  isFinishing = false,
  onExportTests,
  onSessionEmail,
  onComplete,
}) {
  const [gateOpen, setGateOpen] = useState(false);
  const tested = totalAssets - remaining;

  const progressLine = remaining > 0
    ? `${tested} of ${totalAssets} assets tested — ${remaining} still need testing.`
    : `All ${totalAssets} assets tested. Ready to complete.`;

  const handleCompletePress = () => {
    if (remaining > 0) {
      setGateOpen(true);
    } else {
      onComplete?.();
    }
  };

  const handleClose = () => {
    setGateOpen(false);
    onClose?.();
  };

  return (
    <BottomSheet open={open} onClose={handleClose}>
      <div className="finish-sheet">

        {/* Header */}
        <div className="finish-sheet__header">
          <h2 className="finish-sheet__title">Finish session</h2>
          <p className="finish-sheet__progress">{progressLine}</p>
        </div>

        {/* Action rows */}
        <div className="finish-sheet__action-group">
          <button
            type="button"
            className="finish-sheet__action-row"
            onClick={onExportTests}
          >
            Export Tests
          </button>
          <button
            type="button"
            className="finish-sheet__action-row finish-sheet__action-row--last"
            onClick={onSessionEmail}
          >
            Session email
          </button>
        </div>

        {/* Inline gate — shown when remaining > 0 and Complete was tapped */}
        {gateOpen && (
          <div className="finish-sheet__gate" role="alert">
            <p className="finish-sheet__gate-text">
              {remaining} not tested — finish anyway?
            </p>
            <div className="finish-sheet__gate-btns">
              <button
                type="button"
                className="finish-sheet__gate-go-back"
                onClick={() => setGateOpen(false)}
              >
                Go back
              </button>
              <button
                type="button"
                className="finish-sheet__gate-finish-anyway"
                onClick={onComplete}
                disabled={isFinishing}
              >
                {isFinishing ? 'Finishing…' : 'Finish anyway'}
              </button>
            </div>
          </div>
        )}

        {/* Primary CTA */}
        <Button
          variant="primary"
          className="session-summary-sheet__cta"
          onClick={handleCompletePress}
          disabled={isFinishing}
        >
          {isFinishing ? 'Finishing…' : 'Complete & Return to Dashboard'}
        </Button>

        {/* Cancel */}
        <button type="button" className="export-sheet__cancel-btn" onClick={handleClose}>
          Cancel
        </button>

      </div>
    </BottomSheet>
  );
}
